import type { IntentResponse } from './api';
import type { ExecutionEvidence, ViewRecipe, WorkspaceEnvelope } from '../components/dynamic-view/types';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isWorkspaceEnvelope(value: unknown): value is WorkspaceEnvelope {
  return isObject(value) && typeof value.target === 'string' && 'payload' in value;
}

function isViewRecipe(value: unknown): value is ViewRecipe {
  return isObject(value) && (
    typeof value.type === 'string' ||
    Array.isArray(value.blocks) ||
    typeof value.html === 'string'
  );
}

export function normalizeWorkspaceEnvelopes(payload: unknown): WorkspaceEnvelope[] {
  if (Array.isArray(payload)) {
    return payload.filter(isWorkspaceEnvelope);
  }

  if (isWorkspaceEnvelope(payload)) {
    return [payload];
  }

  if (isViewRecipe(payload)) {
    return [{ target: 'main_canvas', payload }];
  }

  return [];
}

export function normalizeMainCanvasPayload(response: IntentResponse): ViewRecipe | null {
  const mainCanvas = normalizeWorkspaceEnvelopes(response.envelopes).find(
    envelope => envelope.target === 'main_canvas',
  );

  if (mainCanvas && isViewRecipe(mainCanvas.payload)) {
    return mainCanvas.payload;
  }

  return response.view_recipe || null;
}

export function normalizeDynamicViewPayload(payload: unknown): ViewRecipe | null {
  const mainCanvas = normalizeWorkspaceEnvelopes(payload).find(
    envelope => envelope.target === 'main_canvas',
  );

  if (mainCanvas && isViewRecipe(mainCanvas.payload)) {
    return mainCanvas.payload;
  }

  return isViewRecipe(payload) ? payload : null;
}

export function getExecutionEvidence(recipe: ViewRecipe | null | undefined): ExecutionEvidence | null {
  return recipe?.evidence || null;
}

export function hasTerminalExecutionEvidence(recipe: ViewRecipe | null | undefined): boolean {
  const evidence = getExecutionEvidence(recipe);
  if (!evidence) return false;
  return evidence.truth_state === 'execution_result' && evidence.terminal_actions_allowed !== false;
}
